import { Preferences } from '@capacitor/preferences';
import { Store, StoreValue } from '@ngneat/elf';
import { StateStorage } from '@ngneat/elf-persist-state';
import { selectRequestStatus } from '@ngneat/elf-requests';

import { IdleState, PendingState, RecordKeys, RequestsStatusState, SuccessState } from '@ngneat/elf-requests/src/lib/requests-status';
import { OperatorFunction, map, of, pipe } from 'rxjs';
import { AppError } from '../api';

export type AppRequestStatus = SuccessState | { value: 'error'; error: AppError } | PendingState | IdleState;
export const isDsValid = (ds: ElfDataSource<any>) => !!ds && !ds.loading && !ds.error;

export const getIdleRequestStatus = (): AppRequestStatus => ({ value: 'idle' });

export function selectAppRequestStatus<S extends RequestsStatusState>(key: RecordKeys<S>): OperatorFunction<S, AppRequestStatus> {
  return pipe(
    selectRequestStatus(key),
    map((status) => {
      return status.value === 'error' ? (status as AppRequestStatus) : status;
    }),
  );
}

export function includeKeys<S extends Store, State extends StoreValue<S>>(keys: Array<keyof State>): OperatorFunction<State, Partial<State>> {
  return pipe(
    map((state) => {
      return Object.keys(state).reduce<State>((toSave, key) => {
        if (keys.includes(key)) toSave[key] = state[key];

        return toSave;
      }, {} as State);
    }),
  );
}

export const capacitorStorageStrategy: StateStorage = {
  async getItem<T extends Record<string, any>>(key: string): Promise<T | null | undefined> {
    const { value } = await Preferences.get({ key });
    return value !== null ? (JSON.parse(value) as T) : undefined;
  },
  async setItem(key: string, value: Record<string, any>): Promise<boolean> {
    await Preferences.set({ key, value: JSON.stringify(value) });
    return true;
  },
  async removeItem(key: string): Promise<boolean> {
    await Preferences.remove({ key });
    return true;
  },
};
export type CouldBeCached<T> = T | 'IN_CACHE';
export const IN_CACHE = 'IN_CACHE';
export const IN_CACHE_SOURCE = { returnSource: of(IN_CACHE) };

export interface ElfDataSource<T> {
  loading: boolean;
  error: any;
  data: T;
}
/** [REQUEST CACHE OVERRIDE] START */

// DDJ : Commented this for now since I wanted to do that for network offline too :
// ->  will test this when we implement the offline mode so we don't overthink here

// export enum DATA_SOURCE_TYPE {
//   FromCache = 'Cache',
//   FromAPI = 'API',
// }

// export interface ApiResult<T> {
//   source: DATA_SOURCE_TYPE;
//   data: T;
// }

// export type RequestsCacheState = StateOf<typeof withRequestsCache>;
// export type CacheRecordKeys<S> = S extends {
//   requestsCache: Record<infer K, any>;
// }
//   ? K
//   : string;
// export type CacheState = {
//   value: 'none' | 'partial' | 'full';
//   timestamp?: number;
// };

// export function createRequestsCacheOperatorWithApiResult<S extends RequestsCacheState>(store: Store<StoreDef<S>>) {
//   return function <T>(key: CacheRecordKeys<S>, options: Parameters<typeof skipWhileCached>[2]) {
//     return skipWhileCached<S, T>(store, key, { ...options });
//   };
// }

// export function skipWhileCached<S extends RequestsCacheState, T>(
//   store: Store<StoreDef<S>>,
//   key: OrArray<CacheRecordKeys<S>>,
//   options: { value?: CacheState['value']; cacheGetter: () => any }
// ): OperatorFunction<T, ApiResult<T>> {
//   return function (source: Observable<T>) {
//     if (store.query(isRequestCached(key, { value: options?.value }))) {
//       return of({ source: DATA_SOURCE_TYPE.FromCache, data: options.cacheGetter() } as any);
//     }

//     return source.pipe(map((s) => ({ source: DATA_SOURCE_TYPE.FromAPI, data: s })));
//   };
// }

/** [REQUEST CACHE OVERRIDE] END */
